import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { Button as Button95, Hourglass, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { updateVisuals } from '@/state/uploads';
import { Community } from "@/types/community";
import { setLoggedIn } from '@/state/user';
import { UserInfo } from "@/types/user_info";
import { Image } from 'expo-image';
import { Meta } from '@/components/Meta';
import { PasswordShowHideButton } from '@/components/PasswordShowHideButton';
import { PasswordRequirementsButton } from '@/components/PasswordRequirementsButton';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  inputWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
});

export default function Join() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const emailInput = useRef<RNTextInput>(null);
  const passwordInput = useRef<RNTextInput>(null);
  const router = useRouter();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const dispatch = useDispatch();
  const [newPasswordShown, setNewPasswordShown] = useState(false);

  const { register, control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      email: '',
      password: '',
    }
  });

  const onSubmit = useCallback(async (data: any) => {
    try {
      showSpinner();

      const response = await AppUtilities.fetchUnauthorized(
        "Join.onSubmit",
        "/rabbl/api/createrabblaccount",
        "POST",
        {
          email: data.email,
          password: data.password,
        }
      );
      AppUtilities.login((response as any).body);
      const creating_community: Community = ((response as any).body as any).creating_community;
      const user_info: UserInfo = ((response as any).body as any).user_info;
      // This also does the login in redux based on the loggedIn response body attribute
      dispatch(setData(response));
      // Reset visuals if we previously created a Rabbl
      dispatch(updateVisuals([]));
      router.replace(AppUtils.getRabblCreateNextPageLink(creating_community, user_info) as RelativePathString);

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      AppUtilities.handleException(e, "Join.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [communityMembership]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (emailInput && emailInput.current) {
        emailInput.current?.focus();
      }
    }
  }, [emailInput]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Create account"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <AppText>Create new account</AppText>

          <Controller
            control={control}
            name="email"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={AppConstants.STYLES.passwordInputRow}>
                <TextInput
                  ref={emailInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Email"
                  style={styles.formElement}
                  returnKeyType="next"
                  onBlur={onBlur}
                  autoComplete="email"
                  keyboardType="email-address"
                  textContentType="emailAddress"
                  autoCapitalize='none'
                  onSubmitEditing={() => passwordInput.current?.focus()}
                />
                {AppUtils.PASSWORD_REQUIREMENTS_BLANK}
              </View>
            )}
          />
          {errors.email && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ Email is required. ^</AppText>}

          <Controller
            control={control}
            name="password"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={AppConstants.STYLES.passwordInputRow}>
                <TextInput
                  ref={passwordInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Password"
                  style={styles.formElement}
                  returnKeyType="go"
                  onBlur={onBlur}
                  autoComplete="password"
                  textContentType="password"
                  onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                  secureTextEntry={newPasswordShown ? false : true}
                />
                <PasswordRequirementsButton />
                <PasswordShowHideButton passwordShown={newPasswordShown} setPasswordShown={setNewPasswordShown} />
              </View>
            )}
          />
          {errors.password && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ Password is required. ^</AppText>}

          <View style={styles.inputWrapper}>
            <Button95 onPress={handleSubmit(onSubmit, onValidationErrors)}>Create</Button95>
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
